/* eslint-disable max-lines-per-function */

import { yupResolver } from '@hookform/resolvers/yup';
import { Clock, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AdditionalServiceInfoModal from 'src/app/AddressBook/Modals/AdditionalServiceInfoModal';
import ServiceLabel from 'src/app/MyQuote/BookingStepper/components/ServiceLabel';
import { DELIVERY_WINDOW_TOOLTIP, PICKUP_WINDOW_TOOLTIP } from 'src/app/MyQuote/CreateQuote/Steps/Steps.const';
import AlertMessage from 'src/components/AlertMessage';
import ButtonCmp from 'src/components/ButtonCmp';
import CardWithOptions from 'src/components/CardWithOptions';
import CheckBox from 'src/components/CheckBox';
import DateSelect from 'src/components/DateSelect/DateSelect';
import DeleteModal from 'src/components/DeleteModal';
import InputMaskCmp from 'src/components/InputMaskCmp';
import InputTextArea from 'src/components/InputTextArea';
import MapBox from 'src/components/MapBox/MapBox';
import SelectBox from 'src/components/SelectBox/SelectBox';
import ShipperConsigneeAutocomplete from 'src/components/ShipperConsigneeAutocomplete/ShipperConsigneeAutocomplete';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { useAddressDetails } from 'src/hooks/useAddressDetails';
import { selectFormData, setQuoteBookingFormData } from 'src/redux/bookingStepper.slice';
import { consigneeOrderInfo, setCreateOrderStep, shipperOrderInfo } from 'src/redux/CreateOrders.slice';
import { RootState } from 'src/redux/store';
import { deleteSingleAddress } from 'src/services/AddressBookServices';
import { deleteShipperContact } from 'src/services/ShipperService';
import { createDateWithoutTimezoneConversion, formatTimeInput, GetAppearance, getDateWithSuffixFormat, getFormattedDateIgnoringTime, getSelectBoxOptions, usePhone } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';
import * as Yup from 'yup';

import { DATE_TYPE, DELIVERY_DATE_TYPE_OPTIONS, DELIVERY_WINDOW_TYPE_OPTIONS, PICKUP_DATE_TYPE_OPTIONS, PICKUP_WINDOW_TYPE_OPTIONS, WINDOW_TYPE } from '../../../AddQuote/Steps/addQuoteSteps.const';
import ShipperConsigneeForm from '../../../CreateQuote/Steps/ShipperConsigneeDetails/ShipperConsigneeForm';
import { BOOKING_KEYS as ORDER_KEYS } from '../../bookingQuote.const';

import { IShipperConsigneeDetailsFormData, IShipperConsigneeDetailsProps } from './ShipperConsigneeDetails.interface';
import ShipperContactForm from './ShipperContactForm';

const isBeforePickupWindowEnd = (value: Date | string | null | undefined, pickupWindowEndDate: string | null | undefined) => {
    if (!value || !pickupWindowEndDate) return false;

    return moment(value).format('YYYY-MM-DD') < moment(pickupWindowEndDate).format('YYYY-MM-DD');
};

const getValidationSchema = (isShipper: boolean, t: (key: string) => string, pickupWindowEndDate: string | null) =>
    Yup.object().shape({
        id: Yup.number().nullable(),
        companyName: Yup.string().notRequired(), // Not a form field, comes from displayData
        fullAddress: Yup.string().notRequired(), // Not a form field, comes from displayData
        expectedSensitivity: Yup.string().required(isShipper ? 'Pickup sensitivity is required' : 'Delivery sensitivity is required'),
        expectedPickupTime: isShipper
            ? Yup.string().required('Pickup date is required')
            : Yup.string()
                  .nullable()
                  .test('required-when-end-filled', t('QuotingHub.CreateQuote.Delivery_Start_Date_Required'), function (value) {
                      const { expectedSensitivity, expectedPickupEndTime } = this.parent;
                      if (expectedSensitivity === DATE_TYPE.FLEXIBLE && expectedPickupEndTime) return !!value;

                      return true;
                  })
                  .test('not-before-pickup-window-end', t('ValidationMessages.Delivery_date_cannot_be_before_pickup_date'), function (value) {
                      const { expectedSensitivity } = this.parent;

                      // OPEN disables the date field, so never block Next on a date the user cannot edit
                      if (expectedSensitivity === DATE_TYPE.OPEN) return true;

                      return !isBeforePickupWindowEnd(value, pickupWindowEndDate);
                  }),
        expectedPickupEndTime: isShipper
            ? Yup.string()
                  .nullable()
                  .when('expectedSensitivity', {
                      is: DATE_TYPE.FLEXIBLE,
                      then: (schema) => schema.required('End date is required for flexible pickup'),
                      otherwise: (schema) => schema.notRequired().nullable(),
                  })
            : Yup.string()
                  .nullable()
                  .when('expectedSensitivity', {
                      is: DATE_TYPE.FLEXIBLE,
                      then: (schema) =>
                          schema
                              .test('required-if-delivery-start-date', t('ValidationMessages.End_Date_is_required'), function (value) {
                                  const { expectedPickupTime } = this.parent;
                                  if (expectedPickupTime && !value) return false;

                                  return true;
                              })
                              .test('not-before-pickup-window-end', t('ValidationMessages.Delivery_date_cannot_be_before_pickup_date'), function (value) {
                                  return !isBeforePickupWindowEnd(value, pickupWindowEndDate);
                              }),
                      otherwise: (schema) => schema.notRequired().nullable(),
                  }),
        shipperAddress: Yup.mixed().nullable(),
        contactId: Yup.string().notRequired().nullable(),
        contactName: Yup.string().notRequired().nullable(),
        email: Yup.string().email('Invalid email format').notRequired().nullable(),
        phoneNumber: Yup.string().notRequired().nullable(),
        isWindow: Yup.string().required('Pickup window type is required'),
        isAppointment: Yup.boolean().default(false),
        startDateTime: Yup.string()
            .notRequired()
            .nullable()
            .test('required-when-end-filled', isShipper ? t('QuotingHub.CreateQuote.Pickup_Start_Time_Required') : t('QuotingHub.CreateQuote.Delivery_Start_Time_Required'), function (value) {
                const { endDateTime, isWindow } = this.parent;
                const relevantWindowType = isShipper ? WINDOW_TYPE.READY_TIME : WINDOW_TYPE.DELIVERY_TIME;

                if (isWindow === relevantWindowType && endDateTime) return !!value;

                return true;
            }),
        endDateTime: Yup.string()
            .notRequired()
            .nullable()
            .test('required-if-start-time', isShipper ? 'End time is required' : t('QuotingHub.CreateQuote.Delivery_End_Time_Required'), function (value) {
                const { startDateTime, isWindow } = this.parent;
                const relevantWindowType = isShipper ? WINDOW_TYPE.READY_TIME : WINDOW_TYPE.DELIVERY_TIME;
                if (isWindow !== relevantWindowType) return true;
                if (startDateTime && !value) return false;

                return true;
            })
            .test('is-after-start', 'End time must be after start time', function (endTime) {
                const { startDateTime, isWindow } = this.parent;

                // Only validate when isWindow matches the relevant type for each side
                const relevantWindowType = isShipper ? WINDOW_TYPE.READY_TIME : WINDOW_TYPE.DELIVERY_TIME;

                if (isWindow !== relevantWindowType) return true;

                if (!startDateTime || !endTime) return true;

                const [startHour, startMin] = startDateTime.split(':').map(Number);
                const [endHour, endMin] = endTime.split(':').map(Number);
                const startMinutes = startHour * 60 + startMin;
                const endMinutes = endHour * 60 + endMin;

                return endMinutes > startMinutes;
            }),
        shipperPickupAppointmentStatus: Yup.string().notRequired(),
        carrierPickupAppointmentStatus: Yup.string().notRequired(),
        pickupInstruction: Yup.string().notRequired(),
        deliveryInstruction: Yup.string().notRequired(),
        addressMismatch: Yup.string().notRequired(),
    });

const ShipperConsigneeDetails = ({ isShipper = true, stepperFooterRef, onEditStateChange }: IShipperConsigneeDetailsProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const shipperOrderInfoData = useSelector(shipperOrderInfo);
    const consigneeOrderInfoData = useSelector(consigneeOrderInfo);
    const bookingFormData = useSelector(selectFormData);
    const { serviceTypes } = useSelector((state: RootState) => state.basic);
    const appearance = GetAppearance();
    const { getFormatPhoneWithPlus } = usePhone();

    // Load address book list (ensures shippers/consignees are fetched if not yet loaded)
    const { shipperOptions, consigneeOptions, handleSearchShipper, handleSearchConsignee } = useAddressDetails();

    // Get shippers and consignees from Redux
    const { shippers, consignees } = useSelector((state: RootState) => state.addresses);

    // Get dynamic data from Redux store
    const formData = isShipper ? bookingFormData?.shipperDetails : bookingFormData?.consigneeDetails;

    // Map Redux data structure to component expected format
    const addressData = formData
        ? {
              id: isShipper ? formData.shipperId : formData.consigneeId,
              companyName: isShipper ? formData.shipperCompanyName : formData.consigneeCompanyName,
              fullAddress: isShipper ? formData.shipperFullAddress : formData.consigneeFullAddress,
              city: isShipper ? formData.shipperCity : formData.consigneeCity,
              state: isShipper ? formData.shipperState : formData.consigneeState,
              stateCode: isShipper ? formData.shipperStateCode : formData.consigneeStateCode,
              postal: isShipper ? formData.shipperPostal : formData.consigneePostal,
              country: isShipper ? formData.shipperCountry : formData.consigneeCountry,
              countryCode: isShipper ? formData.shipperCountryCode : formData.consigneeCountryCode,
              latitude: isShipper ? parseFloat(formData.shipperLatitude) || 0 : parseFloat(formData.consigneeLatitude) || 0,
              longitude: isShipper ? parseFloat(formData.shipperLongitude) || 0 : parseFloat(formData.consigneeLongitude) || 0,
              timezone: appearance.timezone || 'America/Toronto',
              streetNumber: isShipper ? formData.shipperStreetNumber : formData.consigneeStreetNumber,
              address1: isShipper ? formData.shipperAddress1 : formData.consigneeAddress1,
          }
        : null;

    // Use addressData or fallback to empty object
    const displayData = addressData || {
        id: null,
        companyName: '',
        fullAddress: '',
        city: '',
        state: '',
        postal: '',
        country: '',
        latitude: 0,
        longitude: 0,
        timezone: appearance.timezone || 'America/Toronto',
    };

    // For consignee, a delivery date can be no earlier than the date the pickup window CLOSES. For a
    // flexible pickup that is pickupEndDate, not pickupDate — anchoring on the start date let users pick
    // a delivery date inside the pickup window (e.g. pickup 18–19 allowed a delivery of 18).
    const pickupWindowEndDate = !isShipper
        ? (bookingFormData?.shipperDetails?.pickupSensitivity === DATE_TYPE.FLEXIBLE && bookingFormData?.shipperDetails?.pickupEndDate
              ? bookingFormData?.shipperDetails?.pickupEndDate
              : bookingFormData?.shipperDetails?.pickupDate) ?? null
        : null;

    // Validation schema
    const validationSchema = useMemo(() => getValidationSchema(isShipper, t, pickupWindowEndDate), [isShipper, t, pickupWindowEndDate]);

    const contactRef = useRef<HTMLParagraphElement>(null);
    // Preserve date when temporarily switching to OPEN sensitivity so it can be restored
    const [savedPickupDate, setSavedPickupDate] = useState<string | null>(null);

    // Initialize react-hook-form
    const {
        control,
        setValue,
        watch,
        getValues,
        trigger,
        setError,
        clearErrors,
        handleSubmit,
        formState: { errors, isSubmitted },
    } = useForm<IShipperConsigneeDetailsFormData>({
        resolver: yupResolver(validationSchema) as any,
        defaultValues: {
            id: displayData.id || null,
            expectedSensitivity: isShipper ? formData?.pickupSensitivity || DATE_TYPE.FIXED : formData?.deliverySensitivity || DATE_TYPE.FIXED,
            expectedPickupTime: isShipper
                ? formData?.pickupDate || shipperOrderInfoData?.expectedPickupTime || moment().add(1, 'day').format('YYYY-MM-DD')
                : formData?.deliveryDate || consigneeOrderInfoData?.expectedPickupTime || null,
            expectedPickupEndTime: isShipper ? formData?.pickupEndDate || null : formData?.deliveryEndDate,
            companyName: displayData.companyName || '',
            shipperAddress: null,
            contactId: isShipper ? formData?.shipperContactId?.toString() || null : formData?.consigneeContactId?.toString() || null,
            contactName: isShipper ? formData?.shipperContactName || '' : formData?.consigneeContactName || '',
            email: isShipper ? formData?.shipperContactEmail || '' : formData?.consigneeContactEmail || '',
            phoneNumber: isShipper ? formData?.shipperContactPhone || formData?.shipperContactNumber || '' : formData?.consigneeContactPhone || formData?.consigneeContactNumber || '',
            isWindow: isShipper ? formData?.pickupTimeType || WINDOW_TYPE.OPEN : formData?.deliveryTimeType || WINDOW_TYPE.OPEN,
            isAppointment: false,
            startDateTime: isShipper ? formData?.pickupStartTime || null : formData?.deliveryStartTime || null,
            endDateTime: isShipper ? formData?.pickupEndTime || null : formData?.deliveryEndTime || null,
            pickupInstruction: formData?.pickupInstruction || '',
            deliveryInstruction: formData?.deliveryInstruction || '',
        },
    });

    // Watch form values
    const watchContactId = watch('contactId');
    const watchContactName = watch('contactName');
    const watchEmail = watch('email');
    const watchPhoneNumber = watch('phoneNumber');
    const watchIsWindow = watch('isWindow');
    const watchExpectedPickupTime = watch('expectedPickupTime');
    const watchExpectedPickupEndTime = watch('expectedPickupEndTime');
    const watchExpectedSensitivity = watch('expectedSensitivity');
    const watchShipperAddress = watch('shipperAddress');
    const watchCompanyName = watch('companyName');

    const deliveryMinDate = useMemo(() => {
        const today = moment.tz(appearance.timezone);

        if (pickupWindowEndDate) {
            const pickupMoment = moment(pickupWindowEndDate);

            return createDateWithoutTimezoneConversion(pickupMoment.isBefore(today, 'day') ? today : pickupMoment);
        }

        return createDateWithoutTimezoneConversion(today);
    }, [pickupWindowEndDate, appearance.timezone]);

    // Contact management state
    const [isEditContact, setIsEditContact] = useState(false);
    const [shipperContactOptions, setShipperContactOptions] = useState<any[]>([]);
    const [shipperContacts, setShipperContacts] = useState<any[]>([]);
    const [deleteAction, setDeleteAction] = useState<{
        mode: string;
        contactId: number | null;
    }>({ mode: '', contactId: null });
    const [isDeleting, setIsDeleting] = useState(false);

    // Address management state
    const [isEditAddress, setIsEditAddress] = useState(false);
    const [deleteAddressAction, setDeleteAddressAction] = useState({ mode: '' });
    const [isDeletingAddress, setIsDeletingAddress] = useState(false);

    // Notify parent so it can disable the Next button while the address form is open
    useEffect(() => {
        onEditStateChange?.(isEditAddress);
    }, [isEditAddress, onEditStateChange]);

    const [isShowAdditionalServiceInfoModal, setIsShowAdditionalServiceInfoModal] = useState(false);

    // Original address set once in Redux when booking starts (from QuoteDetail)
    const originalAddress = isShipper ? bookingFormData?.originalAddresses?.shipper : bookingFormData?.originalAddresses?.consignee;

    const handlePickupDateChange = (event: any) => {
        setValue('expectedPickupTime', moment(event.value).format('YYYY-MM-DD'));
    };

    const handleWindowTypeChange = (val: string, onChange: any) => () => {
        onChange(val);

        if (val === WINDOW_TYPE.OPEN) {
            setValue('startDateTime', null);
            setValue('endDateTime', null);
        } else if (val === WINDOW_TYPE.APPOINTMENT) {
            setValue('endDateTime', null);
        }

        if (isSubmitted) {
            trigger('startDateTime');
            trigger('endDateTime');
        }
    };

    const handleTimeInputChange = (event: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void, previousValue: string, fieldName?: 'startDateTime' | 'endDateTime') => {
        let timeValue = event.target.value;
        const formattedTime = formatTimeInput(timeValue, previousValue);
        onChange(formattedTime);

        if (fieldName) {
            clearErrors(fieldName);
        }

        // When end time changes, re-validate start time (it may become required)
        if (fieldName === 'endDateTime' && isSubmitted) {
            trigger('startDateTime');
        }
    };

    // Sync form values with Redux when component mounts or Redux data changes
    useEffect(() => {
        if (formData) {
            const sensitivityValue = isShipper ? formData.pickupSensitivity : formData.deliverySensitivity;

            if (sensitivityValue) {
                setValue('expectedSensitivity', sensitivityValue);
            }

            if (isShipper) {
                // Sync pickup date
                if (formData.pickupDate) {
                    setValue('expectedPickupTime', formData.pickupDate);
                }

                if (formData.pickupEndDate) {
                    setValue('expectedPickupEndTime', formData.pickupEndDate);
                }

                // Sync shipper contact data
                if (formData.shipperContactId) {
                    setValue('contactId', formData.shipperContactId?.toString() || null);
                    setValue('contactName', formData.shipperContactName || '');
                    setValue('email', formData.shipperContactEmail || '');
                    setValue('phoneNumber', formData.shipperContactPhone || formData.shipperContactNumber || '');
                }

                // Sync shipper time data
                if (formData.pickupStartTime) {
                    setValue('startDateTime', formData.pickupStartTime || null);
                    setValue('endDateTime', formData.pickupEndTime || null);
                    setValue('isWindow', formData?.pickupTimeType || WINDOW_TYPE.READY_TIME);
                }

                // Sync instruction
                if (formData.pickupInstruction !== undefined) {
                    setValue('pickupInstruction', formData.pickupInstruction || '');
                }
            } else {
                if (formData.deliveryDate) {
                    setValue('expectedPickupTime', formData.deliveryDate);
                }

                if (formData.deliveryEndDate) {
                    setValue('expectedPickupEndTime', formData.deliveryEndDate);
                }

                // Sync consignee contact data
                if (formData.consigneeContactId) {
                    setValue('contactId', formData.consigneeContactId?.toString() || null);
                    setValue('contactName', formData.consigneeContactName || '');
                    setValue('email', formData.consigneeContactEmail || '');
                    setValue('phoneNumber', formData.consigneeContactPhone || formData.consigneeContactNumber || '');
                }

                // Sync consignee time data
                if (formData.deliveryStartTime) {
                    setValue('startDateTime', formData.deliveryStartTime || null);
                    setValue('endDateTime', formData.deliveryEndTime || null);
                    setValue('isWindow', formData?.deliveryTimeType || WINDOW_TYPE.DELIVERY_TIME);
                }

                // Sync instruction
                if (formData.deliveryInstruction !== undefined) {
                    setValue('deliveryInstruction', formData.deliveryInstruction || '');
                }
            }
        }
    }, [formData, isShipper, setValue]);

    // When Google Places does not return a separate street_number / route component,
    // address1 and streetNumber come back empty even though fullAddress has them.
    // Parse the first comma-segment of fullAddress to fill in the blanks.
    const parseStreetFields = (fullAddress: string, address1?: string, streetNumber?: string): { address1: string; streetNumber: string } => {
        const filledAddress1 = address1 || '';
        const filledStreetNumber = streetNumber || '';

        if (filledAddress1 && filledStreetNumber) return { address1: filledAddress1, streetNumber: filledStreetNumber };

        const firstSegment = (fullAddress || '').split(',')[0]?.trim() || '';

        if (!firstSegment) return { address1: filledAddress1, streetNumber: filledStreetNumber };

        // Leading digits = street number (e.g. "421 Rue Soumande" → "421" + "Rue Soumande")
        const match = firstSegment.match(/^(\d+[\w-]*)\s+(.+)$/);

        if (match) {
            return {
                streetNumber: filledStreetNumber || match[1],
                address1: filledAddress1 || match[2],
            };
        }

        return { streetNumber: filledStreetNumber, address1: filledAddress1 || firstSegment };
    };

    // Initialize shipperAddress form field from Redux display data on mount.
    // Also enrich address1/streetNumber from fullAddress so the Address modal
    // shows pre-filled values when the user opens an existing shipper/consignee.
    useEffect(() => {
        if (displayData?.fullAddress) {
            const existingAddress1 = isShipper ? formData?.shipperAddress1 : formData?.consigneeAddress1;
            const existingStreetNumber = isShipper ? formData?.shipperStreetNumber : formData?.consigneeStreetNumber;
            const { address1, streetNumber } = parseStreetFields(displayData.fullAddress, existingAddress1, existingStreetNumber);

            setValue('shipperAddress', {
                id: displayData.id,
                companyName: displayData.companyName,
                fullAddress: displayData.fullAddress,
                address1,
                streetNumber,
                city: displayData.city,
                state: displayData.state,
                stateCode: isShipper ? formData?.shipperStateCode || '' : formData?.consigneeStateCode || '',
                country: displayData.country,
                countryCode: isShipper ? formData?.shipperCountryCode || '' : formData?.consigneeCountryCode || '',
                postal: displayData.postal,
                latitude: String(displayData.latitude || ''),
                longitude: String(displayData.longitude || ''),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Resolve timezone from the matched shipper/consignee in Redux
    const addressTimezone = useMemo(() => {
        const currentId = watchShipperAddress?.id;

        if (!currentId) return null;

        const matched = isShipper ? shippers.find((s: any) => s.id === currentId) : consignees.find((c: any) => c.id === currentId);

        return matched?.timezone || matched?.timeZone || null;
    }, [watchShipperAddress?.id, shippers, consignees, isShipper]);

    const selectedEntityBusinessHours = useMemo(() => {
        const currentId = watchShipperAddress?.id;

        if (!currentId) return null;

        const entity = isShipper ? shippers?.find((s: any) => s.id === currentId) : consignees?.find((c: any) => c.id === currentId);
        const start = entity?.businessHoursStartTime ? String(entity.businessHoursStartTime).slice(0, 5) : null;
        const end = entity?.businessHoursEndTime ? String(entity.businessHoursEndTime).slice(0, 5) : null;

        return start && end ? `${start} – ${end}` : null;
    }, [watchShipperAddress?.id, shippers, consignees, isShipper]);

    // Extract contacts when shipper/consignee ID is available
    useEffect(() => {
        const currentId = watchShipperAddress?.id;

        if (currentId) {
            const data = isShipper ? shippers.find((s: any) => s.id === currentId) : consignees.find((c: any) => c.id === currentId);

            if (data?.contacts) {
                const contactsOptions = getSelectBoxOptions(data.contacts, 'id', 'name', true);
                setShipperContactOptions(contactsOptions || []);
                setShipperContacts(data.contacts || []);
            }
        }
    }, [watchShipperAddress?.id, shippers, consignees, isShipper]);

    // Contact card options
    const contactCardOptions = useMemo(
        () => [
            {
                label: t(isShipper ? 'Orders.Order_Stepper.Shipper_Details.Switch_Shipper_Contact' : 'Orders.Order_Stepper.Consignee_Details.Switch_Consignee_Contact'),
                value: 'switch_contact',
            },
            {
                label: t(isShipper ? 'Orders.Order_Stepper.Shipper_Details.Edit_Shipper_Contact' : 'Orders.Order_Stepper.Consignee_Details.Edit_Consignee_Contact'),
                value: 'edit_contact',
            },
            {
                label: t(isShipper ? 'Orders.Order_Stepper.Shipper_Details.Delete_Shipper_Contact' : 'Orders.Order_Stepper.Consignee_Details.Delete_Consignee_Contact'),
                value: 'delete_contact',
            },
        ],
        [t, isShipper]
    );

    const updateReduxAddress = useCallback(
        (selected: any) => {
            const updatedFormData: any = { ...bookingFormData };

            if (isShipper) {
                updatedFormData.shipperDetails = {
                    ...bookingFormData?.shipperDetails,
                    shipperId: selected.id || null,
                    shipperCompanyName: selected.companyName || '',
                    shipperFullAddress: selected.fullAddress || selected.address?.fullAddress || '',
                    shipperAddress1: selected.address1 || selected.address?.address1 || '',
                    shipperStreetNumber: selected.streetNumber || selected.address?.streetNumber || '',
                    shipperCity: selected.city || selected.address?.city || '',
                    shipperState: selected.state || selected.address?.state || '',
                    shipperStateCode: selected.stateCode || selected.address?.stateCode || '',
                    shipperCountry: selected.country || selected.address?.country || '',
                    shipperCountryCode: selected.countryCode || selected.address?.countryCode || '',
                    shipperPostal: selected.postal || selected.address?.postal || '',
                    shipperLatitude: selected.latitude?.toString() || selected.address?.latitude || '',
                    shipperLongitude: selected.longitude?.toString() || selected.address?.longitude || '',
                    shipperContactId: null,
                    shipperContactName: '',
                    shipperContactPhone: '',
                    shipperContactEmail: '',
                    shipperContactNumber: '',
                };
            } else {
                updatedFormData.consigneeDetails = {
                    ...bookingFormData?.consigneeDetails,
                    consigneeId: selected.id || null,
                    consigneeCompanyName: selected.companyName || '',
                    consigneeFullAddress: selected.fullAddress || selected.address?.fullAddress || '',
                    consigneeAddress1: selected.address1 || selected.address?.address1 || '',
                    consigneeStreetNumber: selected.streetNumber || selected.address?.streetNumber || '',
                    consigneeCity: selected.city || selected.address?.city || '',
                    consigneeState: selected.state || selected.address?.state || '',
                    consigneeStateCode: selected.stateCode || selected.address?.stateCode || '',
                    consigneeCountry: selected.country || selected.address?.country || '',
                    consigneeCountryCode: selected.countryCode || selected.address?.countryCode || '',
                    consigneePostal: selected.postal || selected.address?.postal || '',
                    consigneeLatitude: selected.latitude?.toString() || selected.address?.latitude || '',
                    consigneeLongitude: selected.longitude?.toString() || selected.address?.longitude || '',
                    consigneeContactId: null,
                    consigneeContactName: '',
                    consigneeContactPhone: '',
                    consigneeContactEmail: '',
                    consigneeContactNumber: '',
                };
            }

            dispatch(setQuoteBookingFormData(updatedFormData));
            setValue('contactId', null);
            setValue('contactName', '');
            setValue('email', '');
            setValue('phoneNumber', '');
        },
        [bookingFormData, isShipper, dispatch, setValue]
    );

    // Matches AddQuote reference: clears shipperAddress form field on switch → SelectBox shows
    const handleAddressCardAction = (value: string) => () => {
        if (value === 'switch_id') {
            setValue('shipperAddress', null);
            setValue('companyName', '');
        } else if (value === 'edit_id') {
            setIsEditAddress(true);
        } else if (value === 'delete_id') {
            setDeleteAddressAction({ mode: 'delete' });
        }
    };

    // Matches AddQuote reference handleShipperChange — sets form fields + syncs Redux
    const handleShipperChange = () => (selectedOption: any) => {
        if (!selectedOption) return;
        const addressList = isShipper ? shippers : consignees;
        const selected = addressList.find((item: any) => item.id === selectedOption.value);

        if (selected) {
            const enriched = { ...selected };

            setValue('shipperAddress', enriched);
            setValue('companyName', selected?.companyName);
            clearErrors('shipperAddress');
        }
    };

    // Matches AddQuote reference handleGoogleAddressSelect
    const handleGoogleAddressSelectForBooking = (addressField: any) => {
        if (!addressField) return;

        // The address is used as returned; parsing street fields here was dropped on purpose.
        const enriched = { ...addressField };

        // Pre-fill the form with whatever Google returned, then open ShipperConsigneeForm
        // so the user can review and complete any missing fields before saving.
        setValue('shipperAddress', {
            fullAddress: enriched.fullAddress ?? '',
            address1: enriched.address1 ?? '',
            city: enriched.city ?? '',
            country: enriched.country ?? '',
            countryCode: enriched.countryCode ?? '',
            latitude: String(enriched.latitude ?? ''),
            longitude: String(enriched.longitude ?? ''),
            postal: enriched.postal ?? '',
            state: enriched.state ?? '',
            stateCode: enriched.stateCode ?? '',
            streetNumber: enriched.streetNumber ?? '',
        });
        setValue('companyName', '');
        clearErrors('shipperAddress');
    };

    const handleSaveAddress = (data: any) => {
        setIsEditAddress(false);

        if (!data) return;

        const { address1, streetNumber } = parseStreetFields(data.fullAddress || '', data.address1, data.streetNumber);
        const enriched = { ...data, address1, streetNumber };

        setValue('shipperAddress', enriched);
        setValue('companyName', enriched.companyName || '');
        clearErrors('shipperAddress');
    };

    const handleEditAddress = (isEdit?: boolean) => () => {
        setIsEditAddress(isEdit ?? true);
    };

    // Matches AddQuote reference: handleShipperConsigneeInputChange
    const handleShipperConsigneeInputChange = (inputValue: string, { action: inputAction }: any) => {
        if (inputAction === 'input-change' || inputAction === 'input-blur' || inputAction === 'input-clear') {
            if (isShipper) {
                handleSearchShipper(inputValue);
            } else {
                handleSearchConsignee(inputValue);
            }
        }
    };

    const handleAddressDeleteModalClose = (closeStatus: boolean) => () => {
        const idToDelete = watchShipperAddress?.id;
        setDeleteAddressAction({ mode: '' });

        if (closeStatus && idToDelete) {
            setIsDeletingAddress(true);
            deleteSingleAddress(idToDelete.toString())
                .then((result: any) => {
                    if (result) {
                        setValue('shipperAddress', null);
                        setValue('companyName', '');
                        updateReduxAddress({
                            id: null,
                            companyName: '',
                            fullAddress: '',
                            address1: '',
                            city: '',
                            state: '',
                            country: '',
                            postal: '',
                            latitude: '',
                            longitude: '',
                        });
                        WalToast.success(`${isShipper ? t('Common.Shipper') : t('Common.Consignee')} removed successfully`);
                    }
                })
                .catch((error: any) => {
                    WalToast.error(error?.response?.data?.message || `Failed to delete ${isShipper ? 'shipper' : 'consignee'}`);
                })
                .finally(() => setIsDeletingAddress(false));
        }
    };

    const handleContact = (onChange: any) => (value: any) => {
        if (value) {
            onChange(value.value?.toString());
            const contactInfo = shipperContacts.find((contact: any) => contact.id === value.value);

            if (contactInfo) {
                setValue('contactName', contactInfo.name || '');
                setValue('email', contactInfo.email || '');
                const rawPhone = contactInfo.number || contactInfo.phoneNumber || '';
                const phoneNumber = rawPhone && !rawPhone.startsWith('+') ? `+${rawPhone}` : rawPhone;
                setValue('phoneNumber', phoneNumber);
            }
        } else {
            onChange(null);
            setValue('contactId', null);
            setValue('contactName', '');
            setValue('email', '');
            setValue('phoneNumber', '');
        }
    };

    const handleCreateNewContact = () => {
        setIsEditContact(true);
        setValue('contactId', null);
        setValue('contactName', '');
        setValue('email', '');
        setValue('phoneNumber', '');
    };

    const handleEditContact = (isEdit?: boolean) => () => {
        setIsEditContact(isEdit ?? true);
    };

    const handleSaveContact = (data: any) => {
        setIsEditContact(false);
        const contactData = data.data?.[0] || data.data;

        if (contactData) {
            setValue('contactId', contactData.id?.toString() || watchContactId);
            setValue('contactName', contactData.name || contactData.contactName || '');
            setValue('email', contactData.email || '');
            setValue('phoneNumber', contactData.number || contactData.phoneNumber || '');

            // Update contacts list
            if (contactData.id) {
                const updatedContacts = shipperContacts.map((contact: any) => (contact.id === contactData.id ? contactData : contact));

                if (!updatedContacts.find((c: any) => c.id === contactData.id)) {
                    updatedContacts.push(contactData);
                }
                setShipperContacts(updatedContacts);
                const updatedOptions = getSelectBoxOptions(updatedContacts, 'id', 'name', true);
                setShipperContactOptions(updatedOptions || []);
            }
        }
    };

    const handleCardActionType = (value: string) => () => {
        if (value === 'edit_contact') {
            setIsEditContact(true);
        } else if (value === 'switch_contact') {
            setValue('contactId', null);
            setValue('contactName', '');
            setValue('email', '');
            setValue('phoneNumber', '');
            setIsEditContact(false);
        } else if (value === 'delete_contact') {
            const contactIdNum = watchContactId ? parseInt(watchContactId) : null;
            setDeleteAction({
                mode: 'delete',
                contactId: contactIdNum,
            });
        }
    };

    const handleModalClose = (closeStatus: boolean) => () => {
        const contactIdToDelete = deleteAction.contactId;

        setDeleteAction({
            mode: '',
            contactId: null,
        });

        if (closeStatus && contactIdToDelete) {
            setIsDeleting(true);
            deleteShipperContact(contactIdToDelete)
                .then((result: any) => {
                    if (result) {
                        // Clear contact form values
                        setValue('contactId', null);
                        setValue('contactName', '');
                        setValue('email', '');
                        setValue('phoneNumber', '');

                        // Filter out the deleted contact from the options
                        setShipperContactOptions((prev: any[]) => prev.filter((contact: any) => contact.value !== contactIdToDelete));
                        setShipperContacts((prev: any[]) => prev.filter((contact: any) => contact.id !== contactIdToDelete));

                        WalToast.success(`${isShipper ? 'Shipper' : 'Consignee'} contact removed`);
                        setIsEditContact(false);
                    }
                })
                .catch((error: any) => {
                    WalToast.error(error?.response?.data?.message || `Error deleting ${isShipper ? 'shipper' : 'consignee'} contact`);
                })
                .finally(() => {
                    setIsDeleting(false);
                });
        }
    };

    const isFullContactInfo = watchContactName && (watchEmail || watchPhoneNumber);

    const hasAddressMismatch = useMemo(() => {
        if (!watchShipperAddress?.fullAddress || !originalAddress?.city) return false;

        return (
            watchShipperAddress.city?.toLowerCase() !== originalAddress.city?.toLowerCase() ||
            watchShipperAddress.state?.toLowerCase() !== originalAddress.state?.toLowerCase() ||
            watchShipperAddress.country?.toLowerCase() !== originalAddress.country?.toLowerCase()
        );
    }, [watchShipperAddress, originalAddress]);

    const handleNext = useCallback(async () => {
        try {
            const formValues = getValues();
            const addressValue = getValues('shipperAddress');

            if (!addressValue?.fullAddress) {
                const msg = isShipper ? 'Please select a shipper address' : 'Please select a consignee address';
                setError('shipperAddress', { type: 'manual', message: msg });
                throw new Error(msg);
            }

            // Validate form fields
            const isValid = await trigger();

            if (!isValid) {
                handleSubmit(() => {})();
                const errorFields = Object.keys(errors);
                const firstError = errorFields[0];
                const firstErrorMessage = errors[firstError as keyof typeof errors]?.message;
                throw new Error(firstErrorMessage || `Please fill in required fields: ${errorFields.join(', ')}`);
            }

            const updatedFormData: any = {
                ...bookingFormData, // Preserve existing data from Redux
            };

            // Read address sub-fields from the live form field (set by updateReduxAddress /
            // parseStreetFields) rather than displayData, which only maps a subset of Redux fields.
            const liveAddress = getValues('shipperAddress') as any;

            if (isShipper) {
                updatedFormData.shipperDetails = {
                    pickupSensitivity: formValues.expectedSensitivity,
                    pickupDate: formValues.expectedPickupTime,
                    pickupEndDate: formValues.expectedPickupEndTime || null,
                    shipperId: liveAddress?.id || null,
                    shipperCompanyName: liveAddress?.companyName || watchCompanyName || '',
                    shipperFullAddress: liveAddress?.fullAddress || '',
                    shipperAddress1: liveAddress?.address1 || formData?.shipperAddress1 || '',
                    shipperStreetNumber: liveAddress?.streetNumber || formData?.shipperStreetNumber || '',
                    shipperCity: liveAddress?.city || '',
                    shipperState: liveAddress?.state || '',
                    shipperStateCode: liveAddress?.stateCode || formData?.shipperStateCode || '',
                    shipperCountry: liveAddress?.country || '',
                    shipperPostal: liveAddress?.postal || '',
                    shipperLatitude: liveAddress?.latitude || '',
                    shipperLongitude: liveAddress?.longitude || '',
                    shipperCountryCode: liveAddress?.countryCode || formData?.shipperCountryCode || '',
                    shipperContactId: formValues.contactId ? parseInt(formValues.contactId) : null,
                    shipperContactName: formValues.contactName,
                    shipperContactPhone: formValues.phoneNumber,
                    shipperContactEmail: formValues.email,
                    pickupInstruction: formValues.pickupInstruction,
                    pickupTimeType: formValues.isWindow,
                    pickupStartTime: formValues.isWindow !== WINDOW_TYPE.OPEN ? formValues.startDateTime : null,
                    pickupEndTime: formValues.isWindow === WINDOW_TYPE.READY_TIME ? formValues.endDateTime : null,
                };
            } else {
                updatedFormData.consigneeDetails = {
                    deliverySensitivity: formValues.expectedSensitivity,
                    deliveryDate: formValues.expectedPickupTime,
                    deliveryEndDate: formValues.expectedPickupEndTime || null,
                    consigneeId: liveAddress?.id || null,
                    consigneeCompanyName: liveAddress?.companyName || watchCompanyName || '',
                    consigneeFullAddress: liveAddress?.fullAddress || '',
                    consigneeAddress1: liveAddress?.address1 || formData?.consigneeAddress1 || '',
                    consigneeStreetNumber: liveAddress?.streetNumber || formData?.consigneeStreetNumber || '',
                    consigneeCity: liveAddress?.city || '',
                    consigneeState: liveAddress?.state || '',
                    consigneeStateCode: liveAddress?.stateCode || formData?.consigneeStateCode || '',
                    consigneeCountry: liveAddress?.country || '',
                    consigneePostal: liveAddress?.postal || '',
                    consigneeLatitude: liveAddress?.latitude || '',
                    consigneeLongitude: liveAddress?.longitude || '',
                    consigneeCountryCode: liveAddress?.countryCode || formData?.consigneeCountryCode || '',
                    consigneeContactId: formValues.contactId ? parseInt(formValues.contactId) : null,
                    consigneeContactName: formValues.contactName,
                    consigneeContactEmail: formValues.email,
                    consigneeContactPhone: formValues.phoneNumber,
                    deliveryTimeType: formValues.isWindow,
                    deliveryStartTime: formValues.isWindow !== WINDOW_TYPE.OPEN ? formValues.startDateTime : null,
                    deliveryEndTime: formValues.isWindow === WINDOW_TYPE.DELIVERY_TIME ? formValues.endDateTime : null,
                    deliveryInstruction: formValues.deliveryInstruction,
                };
            }

            dispatch(setQuoteBookingFormData(updatedFormData));

            await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (error: any) {
            console.error('Error in handleNext:', error);
            throw error;
        }
    }, [getValues, trigger, errors, isShipper, watchExpectedSensitivity, dispatch, displayData, formData, bookingFormData]);

    const handleBack = useCallback(() => {
        const formValues = getValues();
        // Always persist the live address sub-fields so that validateRequiredStepsBeforeBooking
        // has address1/streetNumber even when the user navigates away via sidebar (triggerBack path).
        const liveAddress = getValues('shipperAddress') as any;
        const liveCompanyName = getValues('companyName');

        // Save current state to Redux before going back
        const updatedFormData: any = {
            ...bookingFormData,
        };

        if (isShipper) {
            updatedFormData.shipperDetails = {
                ...bookingFormData?.shipperDetails,
                pickupSensitivity: formValues.expectedSensitivity,
                pickupDate: formValues.expectedPickupTime,
                pickupEndDate: formValues.expectedPickupEndTime || null,
                shipperId: liveAddress?.id || null,
                shipperCompanyName: liveCompanyName || '',
                shipperAddress1: liveAddress?.address1 || bookingFormData?.shipperDetails?.shipperAddress1 || '',
                shipperStreetNumber: liveAddress?.streetNumber || bookingFormData?.shipperDetails?.shipperStreetNumber || '',
                shipperContactId: formValues.contactId ? parseInt(formValues.contactId) : null,
                shipperContactName: formValues.contactName,
                shipperContactPhone: formValues.phoneNumber,
                shipperContactEmail: formValues.email,
                pickupInstruction: formValues.pickupInstruction,
                pickupTimeType: formValues.isWindow,
                pickupStartTime: formValues.isWindow !== WINDOW_TYPE.OPEN ? formValues.startDateTime : null,
                pickupEndTime: formValues.isWindow === WINDOW_TYPE.READY_TIME ? formValues.endDateTime : null,
            };
        } else {
            updatedFormData.consigneeDetails = {
                ...bookingFormData?.consigneeDetails,
                deliverySensitivity: formValues.expectedSensitivity,
                deliveryDate: formValues.expectedPickupTime,
                deliveryEndDate: formValues.expectedPickupEndTime || null,
                consigneeId: liveAddress?.id || null,
                consigneeCompanyName: liveCompanyName || '',
                consigneeAddress1: liveAddress?.address1 || bookingFormData?.consigneeDetails?.consigneeAddress1 || '',
                consigneeStreetNumber: liveAddress?.streetNumber || bookingFormData?.consigneeDetails?.consigneeStreetNumber || '',
                consigneeContactId: formValues.contactId ? parseInt(formValues.contactId) : null,
                consigneeContactName: formValues.contactName,
                consigneeContactPhone: formValues.phoneNumber,
                consigneeContactEmail: formValues.email,
                deliveryInstruction: formValues.deliveryInstruction,
                deliveryTimeType: formValues.isWindow,
                deliveryStartTime: formValues.isWindow !== WINDOW_TYPE.OPEN ? formValues.startDateTime : null,
                deliveryEndTime: formValues.isWindow === WINDOW_TYPE.DELIVERY_TIME ? formValues.endDateTime : null,
            };
        }

        dispatch(setQuoteBookingFormData(updatedFormData));

        dispatch(setCreateOrderStep(isShipper ? null : ORDER_KEYS.SHIPPER_DETAILS));
    }, [getValues, dispatch, isShipper, displayData, bookingFormData]);

    const watchServiceId: number[] = bookingFormData?.services?.serviceId ? bookingFormData.services.serviceId.split(',').filter(Boolean).map(Number) : [];

    const textReplacements = [
        { original: 'After Hours / Weekend / Holiday Pickup', replacement: t('QuotingHub.Common.After_Hours_Pickup') },
        { original: 'Pickup Appointment Required', replacement: t('QuotingHub.Common.Pickup_Appointment') },
        { original: 'Delivery Appointment Required', replacement: t('QuotingHub.Common.Delivery_Appointment') },
    ];

    const getReplacedText = (text: string) => {
        const replacementItem = textReplacements.find((item) => item.original === text);

        return replacementItem ? replacementItem.replacement : text;
    };

    const getServiceList = (data: any[]) => (
        <>
            {data.map((val: any) => (
                <div className="flex w-[calc(33.33%_-_8px)]" key={`service_${val.id}`}>
                    <CheckBox
                        id={`service_${val.id}`}
                        onChangeFunc={() => {}}
                        name={val.slug}
                        value={val.id}
                        checked={watchServiceId.includes(val.id) || false}
                        parentClassName="mt-px"
                        disabled={true}
                    />
                    <div className="leading-[0.9] space-x-1.5 flex items-center truncate pl-2 flex-1">
                        {val.slug === 'pickup_appointment_required' || val.slug === 'delivery_appointment_required' || val.slug === 'after_hours_pickup' ? (
                            <TooltipCmp message={val.name} childrenClassName="!flex">
                                <label htmlFor={`service_${val.id}`} className="truncate leading-4 text-textSecondary text-xs font-medium cursor-not-allowed opacity-50">
                                    {getReplacedText(val.name)}
                                </label>
                            </TooltipCmp>
                        ) : (
                            <ServiceLabel
                                htmlFor={`service_${val.id}`}
                                name={val.name}
                                displayName={getReplacedText(val.name)}
                                labelClassName="leading-4 text-textSecondary text-xs font-medium cursor-not-allowed opacity-50"
                            />
                        )}
                    </div>
                </div>
            ))}
        </>
    );

    useEffect(() => {
        const stepperHandlers = {
            triggerNext: async () => {
                await handleNext();
            },
            triggerBack: async () => {
                await handleBack();
            },
            // Called by the parent stepper when navigating here from a later step
            // to show validation errors without saving or advancing.
            triggerValidation: async () => {
                await trigger();
            },
        };

        // Register handlers with parent via the ref
        if (stepperFooterRef && 'current' in stepperFooterRef) {
            (stepperFooterRef as any).current = stepperHandlers;
        }
    }, [stepperFooterRef, handleBack, handleNext, trigger, isShipper, getValues]);

    return (
        <>
            {/* Company Info - Interactive Card / Address Select / Edit Form */}
            <div className="animate-field-item">
                {isEditAddress ? (
                    <ShipperConsigneeForm
                        handleEditShipper={handleEditAddress}
                        id={
                            // Use watchShipperAddress.id (live form value) instead of displayData.id
                            // (stale Redux) so a freshly selected address is reflected immediately.
                            // Only pass id when it belongs to the user's own address book to avoid
                            // calling updateSingleAddress on records they don't own (403).
                            watchShipperAddress?.id && (isShipper ? shippers.find((s: any) => s.id === watchShipperAddress.id) : consignees.find((c: any) => c.id === watchShipperAddress.id))
                                ? +watchShipperAddress.id
                                : undefined
                        }
                        handleSaveShipper={handleSaveAddress}
                        isShipper={isShipper}
                        defaultCompanyName={watchCompanyName || ''}
                        defaultAddress={(watchShipperAddress as any) || null}
                    />
                ) : (
                    <div className="stepper-content-container">
                        <div className="stepper-lable-wrap flex">
                            <label className="stepper-label-common">{isShipper ? t('Orders.Order_Stepper.Shipper_Details.Shipper') : t('Orders.Order_Stepper.Consignee_Details.Consignee')}</label>
                            <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                        </div>

                        <div className="flex-1 flex flex-col gap-3">
                            <ShipperConsigneeAutocomplete
                                control={control}
                                isShipper={isShipper}
                                isEditId={false}
                                watchCompanyName={watchCompanyName}
                                watchShipperAddress={watchShipperAddress}
                                watchShipperFullAddress={watchShipperAddress?.fullAddress}
                                shipperOptions={shipperOptions}
                                consigneeOptions={consigneeOptions}
                                errors={errors}
                                handleShipperChange={handleShipperChange}
                                handleCreateNew={() => {
                                    setIsEditAddress(true);
                                }}
                                handleCardActionType={handleAddressCardAction}
                                onInputChange={handleShipperConsigneeInputChange}
                                fieldName={isShipper ? 'shipperId' : 'consigneeId'}
                                showDeleteOption={!!watchShipperAddress?.id && !!(isShipper ? shippers : consignees).find((item: any) => item.id === watchShipperAddress?.id)}
                                handleGoogleAddressSelect={handleGoogleAddressSelectForBooking}
                                watchShipperConsigneeId={
                                    watchShipperAddress?.id && (isShipper ? shippers : consignees).find((item: any) => item.id === watchShipperAddress?.id) ? watchShipperAddress.id : null
                                }
                                isQuoteCreateForm={true}
                                showCardOptions={false}
                            />
                            {hasAddressMismatch && (
                                <AlertMessage style="info" labelInfo={`The ${isShipper ? 'shipper' : 'consignee'} address has been updated. Pricing may change based on the new address.`} />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Map Display */}
            {!isEditAddress && (
                <div className="field-without-top-border field-within-same-field !pt-0 animate-field-item">
                    <div className="stepper-content-container">
                        <div className="stepper-lable-wrap flex">
                            <></>
                        </div>

                        <div className="rounded-lg shadow-[0px_4px_8px_-2px_rgba(16,_24,_40,_0.10),_0px_2px_4px_-2px_rgba(16,_24,_40,_0.06)] overflow-hidden w-full">
                            <MapBox
                                draggable={false}
                                address={watchShipperAddress && watchShipperAddress.fullAddress ? watchShipperAddress : null}
                                id={`${isShipper ? 'shipper' : 'consignee'}Map`}
                                className="rounded-lg bg-white h-full border border-utilityGray200"
                                isDefaultSatelliteView={true}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Pickup / Delivery Sensitivity */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        {isShipper ? 'Pickup Sensitivity' : 'Delivery Sensitivity'}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <div className="flex-1">
                    <Controller
                        name="expectedSensitivity"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <div className="flex flex-wrap gap-2">
                                {(isShipper ? PICKUP_DATE_TYPE_OPTIONS : DELIVERY_DATE_TYPE_OPTIONS).map((option) => {
                                    const IconComponent = option.icon;
                                    const isSelected = value === option.value;

                                    return (
                                        <ButtonCmp
                                            key={option.value}
                                            type="button"
                                            className={isSelected ? 'btn-outline-primary' : 'btn_secondary_black'}
                                            onClick={() => {
                                                const currentDate = getValues('expectedPickupTime');

                                                if (option.value === DATE_TYPE.OPEN) {
                                                    // Save date before clearing so it can be restored later
                                                    if (currentDate) setSavedPickupDate(currentDate);
                                                    setValue('expectedPickupTime', null);
                                                } else {
                                                    // Restore saved date when switching away from OPEN
                                                    if (value === DATE_TYPE.OPEN && savedPickupDate) {
                                                        setValue('expectedPickupTime', savedPickupDate);
                                                        setSavedPickupDate(null);
                                                    }
                                                }

                                                onChange(option.value);

                                                if (option.value !== DATE_TYPE.FLEXIBLE) {
                                                    setValue('expectedPickupEndTime', null);
                                                    if (isSubmitted) clearErrors(['expectedPickupEndTime', 'expectedPickupTime']);
                                                }
                                            }}
                                            disabled={isShipper}
                                        >
                                            <IconComponent className="w-4 h-4" />
                                            {option.label}
                                        </ButtonCmp>
                                    );
                                })}
                            </div>
                        )}
                    />
                </div>
            </div>

            {/* Pickup/Delivery Date */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{isShipper ? t('Orders.Order_Stepper.Shipper_Details.Pickup_Date') : t('Orders.Order_Stepper.Consignee_Details.Est_Delivery_Date')}</label>
                    {isShipper && <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>}
                </div>
                {watchExpectedSensitivity === DATE_TYPE.FLEXIBLE ? (
                    // Flexible: start date + "to" + end date side by side
                    <div className="w-full flex items-center gap-3">
                        <Controller
                            name="expectedPickupTime"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <DateSelect
                                    label=""
                                    onChangeFunc={(event: any) => {
                                        onChange(moment(event.value).format('YYYY-MM-DD'));
                                        clearErrors('expectedPickupTime');

                                        if (watchExpectedPickupEndTime && moment(event.value).isAfter(moment(watchExpectedPickupEndTime))) {
                                            setValue('expectedPickupEndTime', null);
                                        }
                                    }}
                                    selected={value}
                                    value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                    placeholder={t('Common.Start_date')}
                                    inputName="expectedPickupTime"
                                    id="expectedPickupTime"
                                    parentClassName="flex-1"
                                    dateFormat="dddd, MMMM Do, YYYY"
                                    minDate={isShipper ? createDateWithoutTimezoneConversion(moment.tz(appearance.timezone)) : deliveryMinDate}
                                    isDisplayIcon={true}
                                    errorText={!!error}
                                    disabled={isShipper}
                                />
                            )}
                        />
                        <Controller
                            name="expectedPickupEndTime"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <DateSelect
                                    label=""
                                    onChangeFunc={(event: any) => {
                                        onChange(moment(event.value).format('YYYY-MM-DD'));
                                        clearErrors('expectedPickupEndTime');
                                    }}
                                    selected={value}
                                    value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                    placeholder={t('Common.End_date')}
                                    inputName="expectedPickupEndTime"
                                    id="expectedPickupEndTime"
                                    parentClassName="flex-1"
                                    dateFormat="dddd, MMMM Do, YYYY"
                                    minDate={
                                        watchExpectedPickupTime
                                            ? createDateWithoutTimezoneConversion(moment(watchExpectedPickupTime))
                                            : isShipper
                                            ? createDateWithoutTimezoneConversion(moment.tz(appearance.timezone))
                                            : deliveryMinDate
                                    }
                                    isDisplayIcon={true}
                                    errorText={!!error}
                                    disabled={isShipper}
                                />
                            )}
                        />
                    </div>
                ) : (
                    <Controller
                        name="expectedPickupTime"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <DateSelect
                                label=""
                                onChangeFunc={(event: any) => {
                                    onChange(moment(event.value).format('YYYY-MM-DD'));
                                    handlePickupDateChange(event);
                                    clearErrors('expectedPickupTime');
                                }}
                                selected={value}
                                value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                placeholder={t('Orders.Order_Stepper.Shipper_Details.Select_date')}
                                inputName="expectedPickupTime"
                                id="expectedPickupTime"
                                parentClassName="w-full"
                                dateFormat="dddd, MMMM Do, YYYY"
                                minDate={isShipper ? createDateWithoutTimezoneConversion(moment.tz(appearance.timezone)) : deliveryMinDate}
                                isDisplayIcon={true}
                                errorText={error?.message ? true : null}
                                disabled={isShipper || watchExpectedSensitivity === DATE_TYPE.OPEN}
                            />
                        )}
                    />
                )}
            </div>

            {/* Pickup / Delivery Window */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{isShipper ? t('BookOrder.Pickup_Window') : t('BookOrder.Delivery_Window')}</label>
                </div>
                <div className="flex gap-x-2 flex-1 flex-col w-full">
                    <div className="flex items-center flex-1 gap-3">
                        <Controller
                            name="isWindow"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <SelectBox
                                    parentClassName="w-[160px]"
                                    options={(isShipper ? PICKUP_WINDOW_TYPE_OPTIONS : DELIVERY_WINDOW_TYPE_OPTIONS).map((opt) => ({ label: opt.label, value: opt.value }))}
                                    value={value ? (isShipper ? PICKUP_WINDOW_TYPE_OPTIONS : DELIVERY_WINDOW_TYPE_OPTIONS).find((opt) => value === opt.value) : null}
                                    onChangeFunc={(selectedOption: any) => handleWindowTypeChange(selectedOption?.value, onChange)()}
                                    isError={!!error}
                                    placeholder={t('Common.Select')}
                                />
                            )}
                        />

                        {watchIsWindow !== WINDOW_TYPE.OPEN && (
                            <Controller
                                name="startDateTime"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <InputMaskCmp
                                        inputName="startDateTime"
                                        placeholder="HH:MM"
                                        parentClassName="flex-1"
                                        value={(value as string) ?? ''}
                                        HandleChange={(e: any) => handleTimeInputChange(e, onChange, (value as string) ?? '', 'startDateTime')}
                                        mask="99:99"
                                        isError={!!error}
                                        icon={
                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                <Clock className="h-4 w-4" />
                                            </div>
                                        }
                                    />
                                )}
                            />
                        )}

                        {(watchIsWindow === WINDOW_TYPE.READY_TIME || watchIsWindow === WINDOW_TYPE.DELIVERY_TIME) && (
                            <Controller
                                name="endDateTime"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <InputMaskCmp
                                        inputName="endDateTime"
                                        placeholder="HH:MM (optional)"
                                        parentClassName="flex-1"
                                        value={(value as string) ?? ''}
                                        HandleChange={(e: any) => handleTimeInputChange(e, onChange, (value as string) ?? '', 'endDateTime')}
                                        mask="99:99"
                                        isError={!!error}
                                        icon={
                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                <Clock className="h-4 w-4" />
                                            </div>
                                        }
                                    />
                                )}
                            />
                        )}
                    </div>
                    {(isShipper ? PICKUP_WINDOW_TOOLTIP : DELIVERY_WINDOW_TOOLTIP)[watchIsWindow as string] && (
                        <p className="text-xs font-normal text-grayLight600 leading-[18px] italic mt-2">
                            {watchIsWindow === WINDOW_TYPE.OPEN && selectedEntityBusinessHours
                                ? (isShipper ? PICKUP_WINDOW_TOOLTIP : DELIVERY_WINDOW_TOOLTIP)[watchIsWindow as string].replace('business hours', `business hours (${selectedEntityBusinessHours})`)
                                : (isShipper ? PICKUP_WINDOW_TOOLTIP : DELIVERY_WINDOW_TOOLTIP)[watchIsWindow as string]}
                        </p>
                    )}
                    {addressTimezone && (
                        <div className="flex-1 space-y-5 mt-3">
                            <SelectBox
                                parentClassName="w-full"
                                name="ShipperTimezone"
                                id="ShipperTimezone"
                                isCloseOnScroll={true}
                                placeholder={addressTimezone}
                                isSearchable={false}
                                className="form_control"
                                menuPosition="fixed"
                                options={[]}
                                value={null}
                                isDisabled={true}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Contact Information - Full CRUD: only show when shipper/consignee ID is available */}
            {watchShipperAddress?.id && (
                <div className="border-t border-utilityGray200 stepper-content-container">
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common">
                            {isShipper ? t('Orders.Order_Stepper.Shipper_Details.Shipper_Contact') : t('Orders.Order_Stepper.Consignee_Details.Consignee_Contact')}
                        </label>
                    </div>
                    <div className="flex-1 space-y-3 stepper-lable-content-wrap ">
                        {!isEditContact && !isFullContactInfo ? (
                            <Controller
                                name="contactId"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <SelectBox
                                        parentClassName="w-full"
                                        name="ContactId"
                                        id="ContactId"
                                        placeholder={t('Common.Select_contact')}
                                        noOptionMessage={t('Common.No_Contact_Found')}
                                        isSearchable={true}
                                        className="form_control"
                                        menuPosition="fixed"
                                        isClearable={true}
                                        options={shipperContactOptions}
                                        onChangeFunc={handleContact(onChange)}
                                        value={value ? shipperContactOptions?.find((e: any) => e?.value == value) : null}
                                        showCustomerImage={true}
                                        menuPlaceOption="auto"
                                        isNewOption={!!watchShipperAddress?.id}
                                        newOptionText={
                                            isShipper ? t('Orders.Order_Stepper.Shipper_Details.Add_New_Shipper_Contact') : t('Orders.Order_Stepper.Consignee_Details.Add_New_Consignee_Contact')
                                        }
                                        handleCreate={handleCreateNewContact}
                                        isDisabled={watchShipperAddress?.id ? false : true}
                                    />
                                )}
                            />
                        ) : null}

                        {isEditContact && (
                            <ShipperContactForm
                                handleEditContact={handleEditContact}
                                contactId={watchContactId || undefined}
                                id={watchShipperAddress?.id ? +watchShipperAddress.id : undefined}
                                shipperContacts={shipperContacts}
                                handleSaveContact={handleSaveContact}
                            />
                        )}

                        {!isEditContact && isFullContactInfo ? (
                            <CardWithOptions
                                handleActionType={handleCardActionType}
                                options={contactCardOptions}
                                headerContentClassName="space-y-2.5 min-w-0"
                                rightAlignOffset
                                tooltipContentFitWidth
                                headerContent={
                                    <>
                                        <h6 className="text-grayLight900 text-sm font-medium word-break">{watchContactName}</h6>
                                        <div className="flex gap-x-3 min-w-0">
                                            <div>
                                                <h6 className="text-gray500 text-xs leading-[18px] font-normal text-nowrap">{t('Common.Contact_Number')}</h6>
                                                <p className="text-grayLight600 text-sm font-medium text-nowrap">{watchPhoneNumber ? getFormatPhoneWithPlus(watchPhoneNumber) : '-'}</p>
                                            </div>
                                            <div className="my-1.5 flex items-center bg-utilityGray200 w-px"></div>
                                            <div className="min-w-0 flex flex-col">
                                                <h6 className="text-gray500 text-xs leading-[18px] font-normal text-nowrap">{t('Common.Contact_Email')}</h6>
                                                <TruncateTextCmp message={watchEmail || '-'} refName={contactRef} tooltipType="action">
                                                    <p className="text-grayLight600 text-sm font-medium text-nowrap truncate" ref={contactRef}>
                                                        {watchEmail || '-'}
                                                    </p>
                                                </TruncateTextCmp>
                                            </div>
                                        </div>
                                    </>
                                }
                            />
                        ) : null}
                    </div>
                </div>
            )}

            {/* Instructions */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap">
                    <label className="stepper-label-common flex">
                        {isShipper ? t('Orders.Order_Stepper.Shipper_Details.Pickup_Instructions') : t('Orders.Order_Stepper.Consignee_Details.Delivery_Instructions')}
                    </label>
                    <p className="text-gray500 text-[12px] leading-[18px] font-normal">{t('Orders.Order_Stepper.Shipper_Details.This_will_appear_on_the_BOLs')}</p>
                </div>
                <div className="flex-1 space-y-5">
                    <Controller
                        name={isShipper ? 'pickupInstruction' : 'deliveryInstruction'}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <InputTextArea
                                labelClassName="form_label block mb-1.5"
                                inputName={isShipper ? 'pickupInstruction' : 'deliveryInstruction'}
                                placeholder={isShipper ? t('Orders.Order_Stepper.Shipper_Details.Enter_pickup_instructions') : t('Orders.Order_Stepper.Consignee_Details.Enter_delivery_instructions')}
                                parentClassName="w-full"
                                className="h-20"
                                value={value || ''}
                                onChangeFunc={(e: any) => {
                                    onChange(e.target.value);
                                }}
                                rows={4}
                                maxLength={200}
                            />
                        )}
                    />
                </div>
            </div>

            {/* Pickup / Delivery Additional Services */}
            <div className="border-t border-utilityGray200 stepper-content-container border-b pb-5">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common flex items-center gap-x-0.5">
                        <span>{isShipper ? t('QuotingHub.Common.Pickup_Additional_Services') : t('QuotingHub.Common.Delivery_Additional_Services')}</span>
                        <div className="cursor-pointer" onClick={() => setIsShowAdditionalServiceInfoModal(true)}>
                            <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                        </div>
                    </label>
                </div>
                <div className="flex-1">
                    <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">
                        {getServiceList(isShipper ? serviceTypes?.PICKUP || [] : serviceTypes?.DELIVERY || [])}
                    </div>
                </div>
            </div>

            {/* Additional Service Info Modal */}
            {isShowAdditionalServiceInfoModal && (
                <AdditionalServiceInfoModal
                    handleClose={() => setIsShowAdditionalServiceInfoModal(false)}
                    services={isShipper ? serviceTypes?.PICKUP || [] : serviceTypes?.DELIVERY || []}
                    title={isShipper ? t('QuotingHub.Common.Pickup_Additional_Services') : t('QuotingHub.Common.Delivery_Additional_Services')}
                />
            )}

            {/* Delete Contact Modal */}
            {deleteAction.mode === 'delete' && (
                <DeleteModal
                    handleClose={handleModalClose}
                    moduleTitle={t('Orders.remove_from_wal_hq', {
                        companyName: watchContactName || '',
                    })}
                    moduleName={`${isShipper ? t('Common.Shipper') : t('Common.Consignee')} Contact`}
                    isDeleteLoading={isDeleting}
                />
            )}

            {/* Delete Address Modal */}
            {deleteAddressAction.mode === 'delete' && (
                <DeleteModal
                    handleClose={handleAddressDeleteModalClose}
                    moduleTitle={t('Orders.remove_from_wal_hq', {
                        companyName: watchShipperAddress?.companyName || watchCompanyName || '',
                    })}
                    moduleName={isShipper ? t('Common.Shipper') : t('Common.Consignee')}
                    isDeleteLoading={isDeletingAddress}
                />
            )}
        </>
    );
};

export default ShipperConsigneeDetails;
